import React, { useState, useEffect } from 'react';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  addBalance,
  updateDiscount,
  getActiveServices,
} from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  MoreVertical,
  Wallet,
  Percent,
  Trash2,
  Power,
  Loader2,
  StickyNote,
  KeyRound,
} from 'lucide-react';
import { ChangePasswordDialog } from '../../components/ChangePasswordDialog';
import UserNotesDialog from '../../components/UserNotesDialog';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export default function UsersManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    price_for_20: 20,
  });
  const [balanceForm, setBalanceForm] = useState({ amount: '', reason: '' });
  const [newPriceFor20, setNewPriceFor20] = useState(20);

  const isSubadmin = currentUser?.role === 'subadmin';

  const REFERENCE_PRICE = 20;
  const RECHARGE_AMOUNTS = [5, 10, 15, 22, 25, 30, 50];
  const [servicesList, setServicesList] = useState([]);

  const priceToDiscount = (price) => ((REFERENCE_PRICE - price) / REFERENCE_PRICE) * 100;
  const discountToPrice = (discount) => REFERENCE_PRICE * (1 - discount / 100);
  const calcServicePrice = (basePrice, priceFor22) => basePrice * (priceFor22 / REFERENCE_PRICE);

  // For subadmin, the minimum price they can set (max discount they can give)
  const minPriceFor20 = isSubadmin ? discountToPrice(currentUser?.discount_percentage || 0) : 0;

  const fetchUsers = async () => {
    try {
      const [usersRes, servicesRes] = await Promise.all([
        getUsers(),
        getActiveServices(),
      ]);
      setUsers(usersRes.data.filter((u) => u.role === 'user'));
      setServicesList(servicesRes.data);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUser.price_for_20 < minPriceFor20) {
      toast.error(`El precio mínimo es $${minPriceFor20.toFixed(2)}`);
      return;
    }
    setSubmitting(true);
    try {
      const discount_percentage = parseFloat(priceToDiscount(newUser.price_for_20).toFixed(2));
      await createUser({
        username: newUser.username,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        discount_percentage,
      });
      toast.success('Usuario creado exitosamente');
      setCreateOpen(false);
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'user',
        price_for_20: 20,
      });
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 
                      Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') :
                      'Error al crear usuario';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      toast.success(
        user.is_active ? 'Usuario desactivado' : 'Usuario activado'
      );
      fetchUsers();
    } catch (err) {
      toast.error('Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`¿Eliminar usuario ${user.username}?`)) return;
    try {
      await deleteUser(user.id);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    if (!balanceForm.amount || parseFloat(balanceForm.amount) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setSubmitting(true);
    try {
      const res = await addBalance(selectedUser.id, {
        amount: parseFloat(balanceForm.amount),
        reason: balanceForm.reason || 'Recarga de saldo',
      });
      toast.success(res.data.message);
      setBalanceOpen(false);
      setBalanceForm({ amount: '', reason: '' });
      fetchUsers();
    } catch (err) {
      toast.error('Error al agregar saldo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDiscount = async (e) => {
    e.preventDefault();
    
    if (newPriceFor20 < minPriceFor20) {
      toast.error(`El precio mínimo es $${minPriceFor20.toFixed(2)}`);
      return;
    }

    const newDiscount = parseFloat(priceToDiscount(newPriceFor20).toFixed(2));

    setSubmitting(true);
    try {
      await updateDiscount(selectedUser.id, { discount_percentage: newDiscount });
      toast.success(`Descuento actualizado a ${newDiscount}%`);
      setDiscountOpen(false);
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Error al actualizar descuento';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-management">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Usuarios</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona los usuarios del sistema
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 btn-glow" data-testid="create-user-btn">
              <Plus className="w-4 h-4" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Crear Usuario</DialogTitle>
              <DialogDescription>
                Crea un nuevo usuario para el sistema
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nombre de usuario</Label>
                <Input
                  id="username"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="new-user-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="new-user-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  className="bg-secondary/50"
                  required
                  data-testid="new-user-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_for_20">Precio para Recarga de $20</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono">$</span>
                  <Input
                    id="price_for_20"
                    type="number"
                    min={minPriceFor20}
                    step="0.01"
                    value={newUser.price_for_20}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        price_for_20: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="bg-secondary/50 pl-7"
                    data-testid="new-user-price-for-20"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {newUser.price_for_20 <= REFERENCE_PRICE ? (
                    <>Descuento calculado: <span className="text-emerald-400 font-bold">{priceToDiscount(newUser.price_for_20).toFixed(2)}%</span></>
                  ) : (
                    <>Recargo calculado: <span className="text-orange-400 font-bold">{Math.abs(priceToDiscount(newUser.price_for_20)).toFixed(2)}%</span></>
                  )}
                  {isSubadmin && <span className="block mt-1 text-yellow-500">Tu precio mínimo: ${minPriceFor20.toFixed(2)}</span>}
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-create-user"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Crear Usuario
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-card border-border/40">
        <CardContent className="p-0">
          {users.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No hay usuarios registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead>Usuario</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Saldo</TableHead>
                  <TableHead>Descuento</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Creado</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow
                    key={user.id}
                    className="border-border/30 hover:bg-secondary/30"
                    data-testid={`user-row-${user.username}`}
                  >
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-accent font-bold">
                        {formatCurrency(user.balance)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-primary font-bold">
                        {user.discount_percentage}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          user.is_active ? 'status-active' : 'status-inactive'
                        }
                      >
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(user.created_at)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`user-menu-${user.username}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-card border-border"
                        >
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setBalanceForm({ amount: '', reason: '' });
                              setBalanceOpen(true);
                            }}
                            data-testid={`add-balance-${user.username}`}
                          >
                            <Wallet className="w-4 h-4 mr-2" />
                            Agregar Saldo
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setNewPriceFor20(parseFloat(discountToPrice(user.discount_percentage).toFixed(2)));
                              setDiscountOpen(true);
                            }}
                            data-testid={`edit-discount-${user.username}`}
                          >
                            <Percent className="w-4 h-4 mr-2" />
                            Ajustar Descuento
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleActive(user)}
                          >
                            <Power className="w-4 h-4 mr-2" />
                            {user.is_active ? 'Desactivar' : 'Activar'}
                          </DropdownMenuItem>
                          {!isSubadmin && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(user);
                                setPasswordOpen(true);
                              }}
                              data-testid={`change-password-${user.username}`}
                            >
                              <KeyRound className="w-4 h-4 mr-2" />
                              Cambiar Contraseña
                            </DropdownMenuItem>
                          )}
                          {!isSubadmin && (
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedUser(user);
                                setNotesOpen(true);
                              }}
                              data-testid={`notes-${user.username}`}
                            >
                              <StickyNote className="w-4 h-4 mr-2" />
                              Notas
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDeleteUser(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Balance Dialog */}
      <Dialog open={balanceOpen} onOpenChange={setBalanceOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Agregar Saldo</DialogTitle>
            <DialogDescription>
              Agregar saldo a {selectedUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBalance} className="space-y-4">
            <div className="space-y-2">
              <Label>Monto</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={balanceForm.amount}
                onChange={(e) =>
                  setBalanceForm({ ...balanceForm, amount: e.target.value })
                }
                className="bg-secondary/50"
                required
                data-testid="balance-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Razón (opcional)</Label>
              <Input
                value={balanceForm.reason}
                onChange={(e) =>
                  setBalanceForm({ ...balanceForm, reason: e.target.value })
                }
                className="bg-secondary/50"
                placeholder="Ej: Pago recibido"
                data-testid="balance-reason-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBalanceOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-balance"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Agregar Saldo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Discount Dialog */}
      <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajustar Descuento</DialogTitle>
            <DialogDescription>
              Descuento actual de {selectedUser?.username}: <span className="text-primary font-bold">{selectedUser?.discount_percentage}%</span>
              {' '}(paga <span className="text-accent font-bold">${discountToPrice(selectedUser?.discount_percentage || 0).toFixed(2)}</span> por la de $20)
              {isSubadmin && (
                <span className="block mt-1 text-yellow-500">
                  Tu precio mínimo: ${minPriceFor20.toFixed(2)} (máx {currentUser?.discount_percentage}%)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateDiscount} className="space-y-4">
            <div className="space-y-2">
              <Label>Precio que pagará por la Recarga de $20</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-lg">$</span>
                <Input
                  type="number"
                  min={minPriceFor20}
                  step="0.01"
                  value={newPriceFor20}
                  onChange={(e) => setNewPriceFor20(parseFloat(e.target.value) || 0)}
                  className="bg-secondary/50 text-lg pl-7 font-mono"
                  data-testid="discount-price-input"
                />
              </div>
            </div>

            <Card className="bg-secondary/30 border-border/30">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between items-center pb-2 border-b border-border/30">
                  <span className="text-muted-foreground">
                    {newPriceFor20 <= REFERENCE_PRICE ? 'Descuento calculado:' : 'Recargo calculado:'}
                  </span>
                  <span className={`text-xl font-bold ${newPriceFor20 <= REFERENCE_PRICE ? 'text-emerald-400' : 'text-orange-400'}`}>
                    {newPriceFor20 <= REFERENCE_PRICE
                      ? `${priceToDiscount(newPriceFor20).toFixed(2)}%`
                      : `+${Math.abs(priceToDiscount(newPriceFor20)).toFixed(2)}%`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground pt-1 pb-2">Precios para todas las recargas:</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm max-h-48 overflow-y-auto">
                  {servicesList.map((svc) => (
                    <div key={svc.id} className="flex justify-between py-1">
                      <span className="text-muted-foreground">{svc.cup_amount} CUP:</span>
                      <span className="font-mono">${calcServicePrice(svc.base_price, newPriceFor20).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDiscountOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting || newPriceFor20 < minPriceFor20}
                data-testid="submit-discount"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Aplicar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} targetUser={selectedUser} />
      {/* User Notes Dialog */}
      <UserNotesDialog
        open={notesOpen}
        onOpenChange={setNotesOpen}
        targetUser={selectedUser}
      />
    </div>
  );
}
